import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Archive,
  Circle,
  MessageSquare,
  MoreHorizontal,
  Send,
  UserCheck,
  X,
} from 'lucide-react';
// Archive used in dropdown menu (disabled), Circle in header, MessageSquare in empty state & dropdown,
// MoreHorizontal in actions, Send in send button, X in mobile close
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { CasinoPanel } from '@/components/CasinoPanel';
import { ReceiptOcrButton } from '@/components/ReceiptOcrButton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
// Card components available if stats cards are added later
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConversations, useMessages, chatKeys } from '@/hooks/useChats';
import { chatApi } from '@/api/chat.api';
import { paymentsApi } from '@/api/payments.api';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/stores/auth.store';
import { getSocket } from '@/sockets/socket';
import type { Conversation, Message } from '@/types/chat.types';
import { formatCurrency } from '@/lib/utils';

// Beep de notificación (Web Audio, sin asset).
let audioCtx: AudioContext | null = null;
function playBeep() {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    audioCtx = audioCtx || new Ctx();
    const o = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    o.connect(g);
    g.connect(audioCtx.destination);
    o.type = 'sine';
    o.frequency.value = 880;
    g.gain.setValueAtTime(0.15, audioCtx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.3);
    o.start();
    o.stop(audioCtx.currentTime + 0.3);
  } catch {
    /* noop */
  }
}

export const Route = createFileRoute('/_authenticated/chats')({
  component: ChatsPage,
});

type StatusFilter = 'all' | 'open' | 'closed' | 'archived';

const statusBadge: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'outline' }> = {
  open: { label: 'Abierto', variant: 'success' },
  closed: { label: 'Cerrado', variant: 'secondary' },
  archived: { label: 'Archivado', variant: 'outline' },
};

function ChatsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sidebarTab, setSidebarTab] = useState<'info' | 'pagos' | 'media'>('info');
  const [localUnread, setLocalUnread] = useState<Record<string, number>>({});
  const [onlyMine, setOnlyMine] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const myId = useAuthStore((s) => s.user?.id);

  const statusParam = status === 'all' ? undefined : status;
  const { data: list, isLoading } = useConversations({ status: statusParam });
  const { data: messages } = useMessages(selectedId || undefined);
  const qc = useQueryClient();

  const flowMutation = useMutation({
    mutationFn: ({ id, flowState }: { id: string; flowState: 'bot' | 'soporte' }) =>
      chatApi.updateConversation(id, { flowState }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.lists() });
      toast.success('Modo actualizado');
    },
    onError: () => toast.error('No se pudo cambiar el modo'),
  });

  const assignMutation = useMutation({
    mutationFn: ({ id, operatorId }: { id: string; operatorId: string | null }) =>
      chatApi.updateConversation(id, { assignedOperator: operatorId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.lists() });
      toast.success('Conversación asignada');
    },
    onError: () => toast.error('No se pudo asignar'),
  });

  const selected = useMemo(() => list?.data?.find((c) => c.id === selectedId) ?? null, [list, selectedId]);

  // Socket.io real-time messages
  useEffect(() => {
    const socket = getSocket();
    const onNewMessage = (msg: Message) => {
      if (msg.conversationId === selectedId) {
        qc.invalidateQueries({ queryKey: chatKeys.messages(selectedId) });
      }
      qc.invalidateQueries({ queryKey: chatKeys.lists() });
      // Mensaje del jugador → sonido + marcar no-leído si no es el chat abierto.
      if (msg.senderType === 'client') {
        playBeep();
        if (msg.conversationId !== selectedId) {
          setLocalUnread((p) => ({ ...p, [msg.conversationId]: (p[msg.conversationId] || 0) + 1 }));
        }
      }
    };
    const onNewConversation = () => {
      toast('Nueva conversación recibida', { icon: '💬' });
    };
    socket.on('message.new', onNewMessage);
    socket.on('conversation.new', onNewConversation);
    return () => {
      socket.off('message.new', onNewMessage);
      socket.off('conversation.new', onNewConversation);
    };
  }, [selectedId]);

  // Join selected conversation room
  useEffect(() => {
    if (!selectedId) return;
    const socket = getSocket();
    socket.emit('conversation.join', { conversationId: selectedId });
  }, [selectedId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Limpiar no-leídos al abrir un chat.
  useEffect(() => {
    if (!selectedId) return;
    setLocalUnread((p) => {
      if (!p[selectedId]) return p;
      const n = { ...p };
      delete n[selectedId];
      return n;
    });
  }, [selectedId]);

  // Título del tab con el total de no-leídos.
  useEffect(() => {
    const total = Object.values(localUnread).reduce((s, n) => s + n, 0);
    document.title = total > 0 ? `(${total}) Chats · Lucky` : 'Chats · Lucky';
  }, [localUnread]);

  const rows: Conversation[] = useMemo(() => {
    let all = list?.data ?? [];
    if (onlyMine && myId) all = all.filter((c) => c.assigned_to_id === myId);
    const q = search.trim().toLowerCase();
    if (q) {
      all = all.filter(
        (c) =>
          (c.client?.nombre ?? '').toLowerCase().includes(q) ||
          (c.client?.apellido ?? '').toLowerCase().includes(q) ||
          (c.client?.casino_username ?? '').toLowerCase().includes(q),
      );
    }
    return all;
  }, [list, search, onlyMine, myId]);

  const columns = useMemo<ColumnDef<Conversation>[]>(() => [
    {
      accessorKey: 'client',
      header: 'Cliente',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {row.original.client?.nombre} {row.original.client?.apellido ?? ''}
          </span>
          <span className="text-xs text-muted-foreground">{row.original.client?.casino_username ?? '—'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const cfg = statusBadge[row.original.status] ?? { label: row.original.status, variant: 'secondary' as const };
        return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
      },
    },
    {
      accessorKey: 'last_message',
      header: 'Último mensaje',
      cell: ({ row }) => (
        <span className="line-clamp-1 text-sm text-muted-foreground">{row.original.last_message ?? '—'}</span>
      ),
    },
    {
      accessorKey: 'unread_count',
      header: '',
      cell: ({ row }) => {
        const n = localUnread[row.original.id] || row.original.unread_count || 0;
        return n > 0 ? (
          <Badge variant="destructive" className="rounded-full px-2 py-0.5 text-xs">
            {n}
          </Badge>
        ) : null;
      },
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setSelectedId(row.original.id)}>
              <MessageSquare className="mr-2 h-4 w-4" />
              Abrir chat
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => myId && assignMutation.mutate({ id: row.original.id, operatorId: myId })}
            >
              <UserCheck className="mr-2 h-4 w-4" />
              Atender yo
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <Archive className="mr-2 h-4 w-4" />
              Archivar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ], [localUnread, myId, assignMutation]);

  const handleSend = async () => {
    if (!selectedId || !input.trim()) return;
    // Enviar por socket: el gateway guarda y emite message.new al room (operador + player lo reciben).
    const socket = getSocket();
    socket.emit('message.send', { conversationId: selectedId, body: input.trim() });
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <>
      <TopBanner
        title="Chats"
        subtitle="Conversaciones en curso"
        searchPlaceholder="Buscar cliente..."
        searchValue={search}
        onSearchChange={setSearch}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Left: conversation list */}
        <div className={`flex flex-col border-r border-border bg-background ${selectedId ? 'hidden w-0 lg:flex lg:w-80 xl:w-96' : 'w-full lg:w-80 xl:w-96'}`}>
          <div className="flex items-center gap-2 border-b border-border p-3">
            {(['all', 'open', 'closed', 'archived'] as StatusFilter[]).map((s) => (
              <Button
                key={s}
                variant={status === s ? 'default' : 'ghost'}
                size="sm"
                className="text-xs capitalize"
                onClick={() => setStatus(s)}
              >
                {s === 'all' ? 'Todas' : statusBadge[s]?.label ?? s}
              </Button>
            ))}
            <Button
              variant={onlyMine ? 'default' : 'ghost'}
              size="sm"
              className="ml-auto gap-1 text-xs"
              onClick={() => setOnlyMine((v) => !v)}
              title="Solo las conversaciones asignadas a mí"
            >
              <UserCheck className="h-3.5 w-3.5" />
              Míos
            </Button>
          </div>
          <div className="flex-1 overflow-auto p-3">
            <DataTable
              columns={columns}
              data={rows}
              isLoading={isLoading}
              pageSize={20}
              emptyMessage="No hay conversaciones"
              onRowClick={(row) => setSelectedId(row.id)}
            />
          </div>
        </div>

        {/* Center: chat messages */}
        <div className={`flex flex-1 flex-col ${selectedId ? 'flex' : 'hidden lg:flex'}`}>
          {selected ? (
            <>
              {/* Chat header */}
              <div className="flex h-14 items-center justify-between border-b border-border px-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {(selected.client?.nombre?.[0] ?? '?').toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium">
                      {selected.client?.nombre} {selected.client?.apellido ?? ''}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Circle className="h-2 w-2 fill-green-500 text-green-500" />
                      {selected.client?.casino_username ?? '—'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={selected.flowState === 'soporte' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() =>
                      flowMutation.mutate({
                        id: selected.id,
                        flowState: selected.flowState === 'soporte' ? 'bot' : 'soporte',
                      })
                    }
                    disabled={flowMutation.isPending}
                    title="🤖 Bot: responde automático · 👤 Manual: lo atendés vos"
                  >
                    {selected.flowState === 'soporte' ? '👤 Manual' : '🤖 Bot'}
                  </Button>
                  <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSelectedId(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-auto space-y-3 p-4">
                {messages?.length ? (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.senderType === 'operator' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                          msg.senderType === 'operator'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-foreground'
                        }`}
                      >
                        {msg.metadata?.imageUrl ? (
                          <div>
                            <a href={msg.metadata.imageUrl} target="_blank" rel="noopener noreferrer">
                              <img
                                src={msg.metadata.imageUrl}
                                alt="comprobante"
                                className="max-h-48 rounded-md"
                              />
                            </a>
                            {msg.senderType !== 'operator' && (
                              <ReceiptOcrButton
                                imageUrl={msg.metadata.imageUrl}
                                clientId={selected.client.id}
                              />
                            )}
                          </div>
                        ) : (
                          <div>{msg.body}</div>
                        )}
                        <div className="mt-1 text-[10px] opacity-70">
                          {new Date(msg.createdAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    Sin mensajes todavía
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="flex items-center gap-2 border-t border-border p-3">
                <Input
                  className="flex-1"
                  placeholder="Escribí un mensaje..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <Button size="icon" onClick={() => void handleSend()} disabled={!input.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground">
              <MessageSquare className="mb-2 h-10 w-10 opacity-30" />
              <p className="text-sm">Seleccioná una conversación para ver los mensajes</p>
            </div>
          )}
        </div>

        {/* Right: client info sidebar */}
        {selected?.client && (
          <div className="hidden w-72 border-l border-border bg-background lg:flex lg:flex-col">
            <div className="border-b border-border p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                  {(selected.client.nombre?.[0] ?? '?').toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-medium">
                    {selected.client.nombre} {selected.client.apellido ?? ''}
                  </div>
                  <div className="text-xs text-muted-foreground">{selected.client.casino_username}</div>
                </div>
              </div>
            </div>
            {/* Tabs del operador */}
            <div className="flex border-b border-border text-xs">
              {(['info', 'pagos', 'media'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSidebarTab(tab)}
                  className={`flex-1 py-2 font-medium capitalize transition-colors ${
                    sidebarTab === tab
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-auto space-y-3 p-4">
              {sidebarTab === 'info' && (
                <>
                  <CasinoPanel clientId={selected.client.id} />
                  <ClientInfo clientId={selected.client.id} />
                </>
              )}
              {sidebarTab === 'pagos' && <ClientPayments clientId={selected.client.id} />}
              {sidebarTab === 'media' &&
                (() => {
                  const imgs = (messages ?? []).filter((m) => m.metadata?.imageUrl);
                  if (!imgs.length)
                    return (
                      <div className="py-8 text-center text-xs text-muted-foreground">
                        Sin comprobantes todavía.
                      </div>
                    );
                  return (
                    <div className="grid grid-cols-2 gap-2">
                      {imgs.map((m) => (
                        <a key={m.id} href={m.metadata!.imageUrl} target="_blank" rel="noopener noreferrer">
                          <img
                            src={m.metadata!.imageUrl}
                            alt="comprobante"
                            className="aspect-square w-full rounded-md border border-border object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  );
                })()}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

interface ClientPayment {
  id: string;
  kind: string;
  amount: string;
  currency: string;
  status: string;
  createdAt: string;
  notes?: string | null;
}

function ClientPayments({ clientId }: { clientId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['payments', 'client', clientId],
    queryFn: () => paymentsApi.list({ clientId }) as Promise<ClientPayment[]>,
    enabled: !!clientId,
  });
  if (isLoading) return <div className="py-6 text-center text-xs text-muted-foreground">Cargando…</div>;
  const payments = data ?? [];
  if (!payments.length)
    return <div className="py-8 text-center text-xs text-muted-foreground">Sin movimientos.</div>;
  const kindLabel: Record<string, string> = {
    deposit: 'Carga',
    withdrawal: 'Retiro',
    bonus: 'Bono',
    adjustment: 'Ajuste',
  };
  const statusColor: Record<string, string> = {
    approved: 'text-green-500',
    pending: 'text-amber-500',
    rejected: 'text-red-500',
    reversed: 'text-muted-foreground',
  };
  return (
    <div className="space-y-2">
      {payments.map((p) => (
        <div key={p.id} className="rounded-lg border border-border p-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-medium">{kindLabel[p.kind] ?? p.kind}</span>
            <span className={statusColor[p.status] ?? ''}>{p.status}</span>
          </div>
          <div className="flex items-center justify-between text-muted-foreground">
            <span className="font-bold text-foreground">{formatCurrency(Number(p.amount), 'PYG')}</span>
            <span>{new Date(p.createdAt).toLocaleDateString('es-AR')}</span>
          </div>
          {p.notes && <div className="mt-1 text-[10px] text-muted-foreground line-clamp-2">{p.notes}</div>}
        </div>
      ))}
    </div>
  );
}

interface FullClient {
  email?: string | null;
  telefono?: string | null;
  estatus: string;
  casinoExternalId?: string | null;
  deviceInfo?: { ip?: string; ua?: string; last_seen?: string } | null;
}

function ClientInfo({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const { data: client } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => apiClient.get(`/admin/clients/${clientId}`).then((r) => r.data as FullClient),
    enabled: !!clientId,
  });
  const blockMut = useMutation({
    mutationFn: (estatus: string) => apiClient.patch(`/admin/clients/${clientId}`, { estatus }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', clientId] });
      toast.success('Cliente actualizado');
    },
    onError: () => toast.error('No se pudo actualizar'),
  });
  if (!client) return null;
  const blocked = client.estatus === 'blocked';
  return (
    <div className="space-y-3">
      <InfoRow label="Email" value={client.email ?? '—'} />
      <InfoRow label="Teléfono" value={client.telefono ?? '—'} />
      <div>
        <div className="text-xs text-muted-foreground">Estado</div>
        <span className={`text-sm font-medium ${blocked ? 'text-red-500' : 'text-green-500'}`}>
          {client.estatus}
        </span>
      </div>
      <InfoRow label="IP" value={client.deviceInfo?.ip || '—'} />
      <InfoRow
        label="Última conexión"
        value={
          client.deviceInfo?.last_seen
            ? new Date(client.deviceInfo.last_seen).toLocaleString('es-AR')
            : '—'
        }
      />
      <Button
        size="sm"
        variant={blocked ? 'outline' : 'destructive'}
        className="w-full"
        onClick={() => blockMut.mutate(blocked ? 'active' : 'blocked')}
        disabled={blockMut.isPending}
      >
        {blocked ? 'Desbloquear usuario' : 'Bloquear usuario'}
      </Button>
    </div>
  );
}
